/**
 * RightRail — Sim.ai-flavored dashboard sidebar.
 *
 * Three tiles, top-to-bottom:
 *   1. Status — lane, cost, elapsed
 *   2. Agents — one row per worker with a status dot + current tool
 *   3. Artifacts — one row per artifact (type chip + name + size)
 *
 * Collapsed by default; toggle from the header `›` button. When the run
 * has no activity yet, the rail hides its sections so the page stays
 * Kimi-quiet.
 *
 * The Status/Agents/Artifacts tiles are split into memoized children that each
 * subscribe to only their own store slice, so a high-frequency `cost_update`
 * token no longer re-renders the agent rows (and vice-versa) during streaming.
 */
import { memo, useMemo } from "react";
import { Bot, FileText, ShieldCheck, Sparkles } from "lucide-react";
import { useShallow } from "zustand/react/shallow";
import { useMultiAgentStore } from "@/stores/multiAgentStore";
import { useArtifactOverlayStore } from "@/stores/multiAgentArtifactOverlayStore";
import { sanitizeArtifactLabel } from "@/services/multi-agent/artifactDisplay";
import { humanizeStatus, humanizeTool } from "@/services/multi-agent";
import { formatBytes } from "@/lib/studio/utils";
import { TimelineTrace } from "@/components/molecules/multi-agent/TimelineTrace";
import { StatusDot } from "@/components/atoms/multi-agent/StatusDot";
import { NODE_DOT } from "@/constants/multi-agent/status.constants";

// Tile 1 — Status / Cost / Lane. Re-renders only on status/lane/cost changes.
const StatusTile = memo(function StatusTile() {
  const status = useMultiAgentStore((s) => s.status);
  const lane = useMultiAgentStore((s) => s.lane);
  const cost = useMultiAgentStore((s) => s.cost);
  return (
    <div className="ma-tile ma-fade-up">
      <div className="ma-tile__title">Status</div>
      <div className="flex items-center gap-2">
        <StatusDot
          status={
            status === "done" ? "ok" :
            status === "failed" ? "err" :
            status === "cancelled" ? "neutral" :
            status === "idle" ? "neutral" : "busy"
          }
          label={null}
        />
        <span style={{ fontSize: 13, fontWeight: 500, color: "var(--ma-text)" }}>
          {status === "idle" ? "Ready" : humanizeStatus(status)}
        </span>
        {lane && (
          <span className="ma-micro ml-auto" style={{ textTransform: "none", letterSpacing: 0 }}>
            {lane === "free" ? "Quick" : `Multi-step · ${humanizeStatus(lane)}`}
          </span>
        )}
      </div>
      {cost && (
        <div
          className="mt-2 flex flex-col gap-1.5"
          style={{ borderTop: "1px solid var(--ma-border)", paddingTop: 8 }}
        >
          {/* Each stat is a single row: label on the left, value on the right */}
          <div className="flex items-baseline justify-between gap-2">
            <span className="ma-micro text-ma-text-muted">Tokens</span>
            <span style={{ fontSize: 14, fontWeight: 600, fontVariantNumeric: "tabular-nums", color: "var(--ma-text)" }}>
              {(cost.tokensIn + cost.tokensOut).toLocaleString()}
            </span>
          </div>
          <div className="ma-micro" style={{ textTransform: "none", letterSpacing: 0, color: "var(--ma-text-muted)", marginTop: -4 }}>
            {cost.tokensIn.toLocaleString()} in · {cost.tokensOut.toLocaleString()} out
          </div>
          <div className="flex items-baseline justify-between gap-2">
            <span className="ma-micro text-ma-text-muted">Tool calls</span>
            <span style={{ fontSize: 14, fontWeight: 600, fontVariantNumeric: "tabular-nums", color: "var(--ma-text)" }}>
              {cost.toolCalls}
            </span>
          </div>
        </div>
      )}
    </div>
  );
});

// Tile 2 — Agents. Re-renders only when the nodes map changes.
const AgentsTile = memo(function AgentsTile() {
  const nodes = useMultiAgentStore((s) => s.nodes);
  const agents = useMemo(() => Object.values(nodes), [nodes]);
  if (agents.length === 0) return null;
  return (
    <div className="ma-tile ma-fade-up">
      <div className="ma-tile__title">
        <Bot className="h-3 w-3" /> Agents · {agents.length}
      </div>
      <ul className="m-0 flex flex-col gap-2 p-0" style={{ listStyle: "none" }}>
        {agents.map((n) => (
          <li
            key={n.id}
            className="flex items-center gap-2"
            style={{ fontSize: 12, color: "var(--ma-text)" }}
            title={n.task}
          >
            <StatusDot status={NODE_DOT[n.status] ?? "neutral"} label={null} />
            <span style={{ textTransform: "lowercase" }}>{n.role}</span>
            {n.toolInFlight && (
              <span
                className="ml-auto truncate"
                style={{
                  fontFamily: "ui-monospace, monospace",
                  fontSize: 11,
                  color: "var(--ma-text-muted)",
                  maxWidth: 140,
                }}
              >
                {humanizeTool(n.toolInFlight)}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
});

// Tile 3 — Artifacts. Re-renders only when the artifacts list changes; the
// last-12-reversed view is memoized so it isn't rebuilt on unrelated renders.
const ArtifactsTile = memo(function ArtifactsTile() {
  const artifacts = useMultiAgentStore((s) => s.artifacts);
  // F12: clicking an artifact in the right panel opens the layover showing its
  // code (close to dismiss) — the panel rows are the trigger.
  const openOverlay = useArtifactOverlayStore((s) => s.open);
  // Show up to the last 50 artifacts; the list scrolls within a bounded height
  // so the rail never grows unbounded.
  const recent = useMemo(() => artifacts.slice(-50).reverse(), [artifacts]);
  if (artifacts.length === 0) return null;
  return (
    <div className="ma-tile ma-fade-up">
      <div className="ma-tile__title">
        <FileText className="h-3 w-3" /> Artifacts · {artifacts.length}
      </div>
      <ul
        className="m-0 flex flex-col gap-1 p-0"
        style={{ listStyle: "none", maxHeight: 300, overflowY: "auto" }}
      >
        {recent.map((a) => {
          const displayName = sanitizeArtifactLabel(a.name, a.id.slice(0, 8));
          return (
            <li key={a.id}>
              <button
                type="button"
                onClick={() => openOverlay(a)}
                className="ma-interactive flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left hover:bg-[var(--ma-surface-soft)]"
                style={{ fontSize: 12, color: "var(--ma-text)" }}
                title={`Open ${displayName}`}
              >
                <span
                  className="ma-micro"
                  style={{
                    padding: "2px 6px",
                    borderRadius: 4,
                    background: "var(--ma-surface-soft)",
                    textTransform: "lowercase",
                    letterSpacing: 0,
                    color: "var(--ma-text-muted)",
                    flex: "none",
                  }}
                >
                  {(a.type || "art").toLowerCase()}
                </span>
                <span className="min-w-0 flex-1 truncate">{displayName}</span>
                <span
                  className="ma-micro"
                  style={{ textTransform: "none", letterSpacing: 0, color: "var(--ma-text-muted)" }}
                >
                  {formatBytes(a.byteSize || 0)}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
});

export function RightRail() {
  const {
    runId, status, nodes,
    verifier, memoryLoaded, sessionContext, sessionId, thread,
  } = useMultiAgentStore(
    useShallow((s) => ({
      runId: s.runId,
      status: s.status,
      nodes: s.nodes,
      verifier: s.verifier,
      memoryLoaded: s.memoryLoaded,
      sessionContext: s.sessionContext,
      sessionId: s.sessionId,
      thread: s.thread,
    }))
  );

  const isFreshEmptySession =
    status === "idle" && !runId && !sessionId && thread.length === 0 && Object.keys(nodes).length === 0;

  const hasContext =
    (memoryLoaded && (memoryLoaded.similarRuns > 0 || memoryLoaded.knownFacts > 0)) ||
    Boolean(sessionContext);

  return (
    <aside
      className="ma-rail flex flex-col"
      style={{ background: "var(--ma-bg)", minWidth: 0, overflowY: "auto" }}
    >
      {/* Rail header */}
      <div className="ma-rail__header flex items-center gap-2">
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--ma-text-muted)" }}>
          Agent Monitor
        </span>
      </div>

      {/* ── Run section ─────────────────────────────────────── */}
      {!isFreshEmptySession && <StatusTile />}
      <AgentsTile />

      {/* ── Outputs section ─────────────────────────────────── */}
      <ArtifactsTile />

      {/* Quality — own section with score as the metric */}
      {verifier && (
        <div className="ma-tile ma-fade-up">
          <div className="ma-tile__title">
            <ShieldCheck className="h-3 w-3" />
            Quality
          </div>
          <div className="flex items-center gap-3">
            <div
              className="ma-tile__metric"
              style={{
                color:
                  verifier.score >= 8
                    ? "var(--ma-state-ok)"
                    : verifier.score >= 6
                      ? "var(--ma-state-warn)"
                      : "var(--ma-state-err)",
              }}
            >
              {verifier.score}/10
            </div>
            <div style={{ fontSize: 11, color: "var(--ma-text-muted)", lineHeight: 1.4 }}>
              {verifier.gaps.length > 0 && (
                <div>{verifier.gaps.length} gap{verifier.gaps.length === 1 ? "" : "s"}</div>
              )}
              {verifier.replanCount > 0 && <div>replanned {verifier.replanCount}×</div>}
              {verifier.noEvidence && (
                <div style={{ color: "var(--ma-state-err)", fontWeight: 600 }}>no evidence</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Context section ─────────────────────────────────── */}
      {hasContext && (
        <div className="ma-rail__group-label">Context</div>
      )}

      {memoryLoaded && (memoryLoaded.similarRuns > 0 || memoryLoaded.knownFacts > 0) && (
        <div className="ma-tile ma-fade-up">
          <div className="ma-tile__title">
            <Sparkles className="h-3 w-3" />
            Memory
          </div>
          <div style={{ fontSize: 12, color: "var(--ma-text-muted)", lineHeight: 1.6 }}>
            {memoryLoaded.similarRuns > 0 && (
              <div>{memoryLoaded.similarRuns} similar run{memoryLoaded.similarRuns === 1 ? "" : "s"}</div>
            )}
            {memoryLoaded.knownFacts > 0 && (
              <div>{memoryLoaded.knownFacts} known fact{memoryLoaded.knownFacts === 1 ? "" : "s"}</div>
            )}
            {memoryLoaded.recentGaps > 0 && (
              <div>{memoryLoaded.recentGaps} gap pattern{memoryLoaded.recentGaps === 1 ? "" : "s"}</div>
            )}
          </div>
        </div>
      )}

      {sessionContext && (
        <div className="ma-tile ma-fade-up">
          <div className="ma-tile__title">
            <Sparkles className="h-3 w-3" />
            Session
          </div>
          <div style={{ fontSize: 12, color: "var(--ma-text-muted)", lineHeight: 1.6 }}>
            <div>{sessionContext.turnCount} prior turn{sessionContext.turnCount === 1 ? "" : "s"}</div>
            {sessionContext.artifactCount > 0 && (
              <div>{sessionContext.artifactCount} artifact{sessionContext.artifactCount === 1 ? "" : "s"}</div>
            )}
            {sessionContext.badToolCount > 0 && (
              <div>{sessionContext.badToolCount} avoided pattern{sessionContext.badToolCount === 1 ? "" : "s"}</div>
            )}
            {sessionContext.compressed && (
              <div>
                compressed
                {typeof sessionContext.keptTurns === "number" && sessionContext.keptTurns > 0
                  ? ` · ${sessionContext.keptTurns} kept`
                  : ""}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Timeline ─────────────────────────────────────────── */}
      <TimelineTrace />

      {isFreshEmptySession && <IdleAgentInvite />}
    </aside>
  );
}

function IdleAgentInvite() {
  return (
    <div className="ma-tile ma-fade-up" style={{ textAlign: "center", padding: "20px 16px" }}>
      <div
        className="mx-auto mb-3 inline-flex items-center justify-center"
        style={{
          width: 36,
          height: 36,
          borderRadius: 999,
          background: "var(--ma-grad-accent)",
          color: "var(--ma-accent)",
          animation: "ma-pulse 2s ease-in-out infinite",
        }}
      >
        <Bot className="h-4 w-4" />
      </div>
      <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ma-text)" }}>
        Agents ready
      </div>
      <div className="ma-micro mt-1" style={{ textTransform: "none", letterSpacing: 0, color: "var(--ma-text-muted)" }}>
        Ask a question and we'll fan out
      </div>
    </div>
  );
}
